import { mean } from 'ramda'
import { type Ref, ref, unref, watch } from 'vue'

export const useAudioLevelMonitor = ({
  minDb,
  stream,
  thresholdMs,
  checkFrequencyMs,
  isAboveMinDBByDefault,
}: {
  minDb: number
  thresholdMs: number
  stream: Ref<MediaStream | undefined>
  checkFrequencyMs: number
  isAboveMinDBByDefault: boolean
}): { isAboveMinDB: Readonly<Ref<boolean>> } => {
  const isAboveMinDB = ref(isAboveMinDBByDefault)
  const audioCtx = new AudioContext()
  const analyserNode = audioCtx.createAnalyser()
  // frequencyDBs stores the dB for each frequency between 0 and half the sample rate (varies depending on device)
  // dB is represented by values between 0 and 255
  // https://developer.mozilla.org/en-US/docs/Web/API/AnalyserNode/getByteFrequencyData
  const frequencyDBs = new Uint8Array(analyserNode.frequencyBinCount)

  const createThresholdTimeout = (): (() => void) => {
    const timeoutId = globalThis.setTimeout(() => (isAboveMinDB.value = false), thresholdMs)
    return () => globalThis.clearTimeout(timeoutId)
  }
  let cancelThresholdTimeout = createThresholdTimeout()

  const createAudioAnalyserInterval = (): (() => void) => {
    const intervalId = globalThis.setInterval(() => {
      analyserNode.getByteFrequencyData(frequencyDBs)
      const averageDBOverAllFrequencies = mean([...frequencyDBs])
      if (averageDBOverAllFrequencies > minDb) {
        isAboveMinDB.value = true
        cancelThresholdTimeout()
        cancelThresholdTimeout = createThresholdTimeout()
      }
    }, checkFrequencyMs)
    return () => globalThis.clearInterval(intervalId)
  }

  watch(
    stream,
    (_old, _new, onCleanup) => {
      const newStream = unref(stream)
      if (!newStream) {
        isAboveMinDB.value = isAboveMinDBByDefault
        return
      }

      analyserNode.getByteTimeDomainData(frequencyDBs)
      const cancelAudioAnalyserInterval = createAudioAnalyserInterval()
      audioCtx.createMediaStreamSource(newStream).connect(analyserNode)
      onCleanup(cancelAudioAnalyserInterval)
    },
    { immediate: true },
  )

  return { isAboveMinDB }
}
